import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Loader2 } from 'lucide-react';

import { toaster, useAppForm } from '@/shared/libs';
import {
  Button,
  CountryFlag,
  Dialog,
  DialogFooter,
  FormField,
  FormInput,
  Label,
  SimpleSelect,
} from '@/shared/ui';
import { FieldError } from '@/shared/ui/form';
import {
  countriesInRegions,
  REGION_META,
  RegionGlyph,
  regionsForCountries,
  useCreateOfferMutation,
  useProviderOfferCapabilities,
  useUpdateOfferMutation,
} from '@entities/Offer';
import {
  toProviderTagOptions,
  useProviderOptions,
  useProviders,
} from '@entities/Provider';
import type {
  Offer,
  OfferDuplicateDraft,
  ProviderOption,
} from '@psp-aggregator/sdk';

import {
  createOfferFormSchema,
  duplicateToFormValues,
  formValuesToOfferInput,
  formValuesToOfferUpdate,
  OFFER_FORM_DEFAULT_VALUES,
  offerFormProgress,
  offerFormStep,
  type OfferFormValues,
  offerToFormValues,
} from '../libs/offerSchema';

import { OfferCoverageSection } from './OfferCoverageSection';
import { OfferFeesSection } from './OfferFeesSection';
import { OfferFormGroup } from './OfferFormGroup';
import { OfferPricingSection } from './OfferPricingSection';

export interface OfferFormModalProps {
  defaultProviderId?: string;
  duplicate?: OfferDuplicateDraft;
  offer?: Offer;
  onClose: () => void;
}

const codesToOptions = (
  codes: readonly string[] | undefined,
  catalog: readonly ProviderOption[] | undefined,
) => {
  const known = new Map(
    toProviderTagOptions(catalog).map((option) => [option.value, option]),
  );

  return (codes ?? []).map(
    (code) => known.get(code) ?? { label: code, value: code },
  );
};

export const OfferFormModal: FC<OfferFormModalProps> = ({
  defaultProviderId,
  duplicate,
  offer,
  onClose,
}) => {
  const { t } = useTranslation();
  const isEdit = Boolean(offer) && !duplicate;
  const createOffer = useCreateOfferMutation();
  const updateOffer = useUpdateOfferMutation();
  const providersQuery = useProviders({ limit: 100, page: 1 });
  const providerOptionsQuery = useProviderOptions();
  const schema = useMemo(() => createOfferFormSchema(t), [t]);
  const form = useAppForm(schema, {
    defaultValues: duplicate
      ? duplicateToFormValues(duplicate)
      : offer
        ? offerToFormValues(offer)
        : {
            ...OFFER_FORM_DEFAULT_VALUES,
            provider_id: defaultProviderId ?? '',
          },
  });
  const values = form.watch();
  const capabilitiesQuery = useProviderOfferCapabilities(values.provider_id);
  const provider = capabilitiesQuery.data;
  const [coverageOpen, setCoverageOpen] = useState(Boolean(values.provider_id));
  const [pricingOpen, setPricingOpen] = useState(Boolean(values.base_currency));
  const [feesOpen, setFeesOpen] = useState(Boolean(values.base_currency));
  const step = offerFormStep(values);
  const progress = offerFormProgress(values);
  const submitting = createOffer.isPending || updateOffer.isPending;

  useEffect(() => {
    if (
      values.currencies.length === 1 &&
      values.base_currency !== values.currencies[0]
    ) {
      form.setValue('base_currency', values.currencies[0]);

      return;
    }

    if (
      values.base_currency &&
      !values.currencies.includes(values.base_currency)
    ) {
      form.setValue('base_currency', '');
    }
  }, [form, values.base_currency, values.currencies]);

  useEffect(() => {
    if (!provider) {
      return;
    }

    const countries = new Set(provider.countries ?? []);
    const currencies = new Set(provider.currencies ?? []);
    const integrations = new Set(provider.integration_methods ?? []);
    const methods = new Set(provider.payment_methods ?? []);
    const implied = new Set<string>([
      ...regionsForCountries(provider.countries ?? []),
      'GLOBAL',
    ]);

    form.setValue(
      'countries',
      form.getValues('countries').filter((code) => countries.has(code)),
    );
    form.setValue(
      'currencies',
      form.getValues('currencies').filter((code) => currencies.has(code)),
    );
    form.setValue(
      'integration_methods',
      form
        .getValues('integration_methods')
        .filter((code) => integrations.has(code)),
    );
    form.setValue(
      'regions',
      form.getValues('regions').filter((code) => implied.has(code)),
    );
    const currentLegs = form.getValues('legs');
    const allowedMethods = (codes: string[]): string[] =>
      codes.filter((code) => methods.has(code));

    form.setValue('legs', {
      ...currentLegs,
      crypto_topup: {
        ...currentLegs.crypto_topup,
        methods: allowedMethods(currentLegs.crypto_topup.methods),
      },
      payin: {
        ...currentLegs.payin,
        methods: allowedMethods(currentLegs.payin.methods),
      },
      payout: {
        ...currentLegs.payout,
        methods: allowedMethods(currentLegs.payout.methods),
      },
      settlement: {
        ...currentLegs.settlement,
        methods: allowedMethods(currentLegs.settlement.methods),
      },
    });
  }, [form, provider]);

  const providerCountries = provider?.countries ?? [];
  const regionOptions = useMemo(
    () =>
      [...regionsForCountries(providerCountries), 'GLOBAL' as const].map(
        (code) => ({
          icon: <RegionGlyph code={code} />,
          label: REGION_META[code].name,
          value: code,
        }),
      ),
    [providerCountries],
  );
  const selectedRegionCountries = new Set(
    countriesInRegions(values.regions).map((code) => code.toUpperCase()),
  );
  const global = values.regions.includes('GLOBAL');
  const inSelectedRegions = (code: string): boolean =>
    values.regions.length === 0 ||
    global ||
    selectedRegionCountries.has(code.toUpperCase());
  const countryOptions = providerCountries
    .filter((code) => inSelectedRegions(code))
    .map((code) => ({
      icon: <CountryFlag code={code} />,
      label: code,
      value: code,
    }));
  const exceptionOptions = providerCountries
    .filter((code) => global || selectedRegionCountries.has(code.toUpperCase()))
    .map((code) => ({
      icon: <CountryFlag code={code} />,
      label: code,
      value: code,
    }));
  const catalog = providerOptionsQuery.data;

  const handleSubmit = form.handleSubmit(
    async (formValues: OfferFormValues) => {
      if (offerFormStep(formValues)) {
        return;
      }

      const payload = formValuesToOfferInput(formValues);

      try {
        if (isEdit && offer) {
          await updateOffer.mutateAsync({
            offerId: offer.id,
            payload: formValuesToOfferUpdate(formValues),
          });
          toaster.success({ title: t('toast.offerUpdated') });
        } else {
          await createOffer.mutateAsync(payload);
          toaster.success({ title: t('toast.offerCreated') });
        }

        onClose();
      } catch (error) {
        toaster.error({
          title: isAxiosError(error)
            ? (error.response?.data?.message ?? t('errors.unknown'))
            : t('errors.unknown'),
        });
      }
    },
  );

  return (
    <Dialog
      title={
        duplicate
          ? t('pages.admin.offers.form.duplicateTitle')
          : isEdit
            ? t('pages.admin.offers.form.editTitle')
            : t('pages.admin.offers.form.createTitle')
      }
      progress={progress}
      width={760}
      onClose={onClose}>
      <form className="flex min-h-0 flex-1 flex-col" onSubmit={handleSubmit}>
        <div className="max-h-[min(64dvh,calc(90dvh-10rem))] space-y-4 overflow-y-auto overscroll-contain p-4 sm:p-6">
          <FormField
            control={form.control}
            name="provider_id"
            render={({ error, field }) => (
              <div className="space-y-1.5">
                <Label className="text-[11px] font-medium text-muted-foreground">
                  {t('pages.admin.offers.fields.provider')}
                  <span className="ml-0.5 text-rose-500">*</span>
                </Label>
                <SimpleSelect
                  aria-label={t('pages.admin.offers.fields.provider')}
                  options={[
                    {
                      label: t('pages.admin.offers.fields.providerPlaceholder'),
                      value: '',
                    },
                    ...(providersQuery.data?.items ?? []).map((item) => ({
                      label: item.alias
                        ? `${item.alias} — ${item.real_name}`
                        : item.real_name,
                      value: item.id,
                    })),
                  ]}
                  fullWidth
                  triggerClassName="h-10"
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    setCoverageOpen(true);
                  }}
                />
                <FieldError error={error} />
              </div>
            )}
          />
          <FormField
            control={form.control}
            name="name"
            render={({ error, field }) => (
              <FormInput
                {...field}
                className="border-border/60 bg-background/60 focus-visible:ring-indigo-500/40 focus-visible:ring-offset-0"
                error={error}
                id="offer-name"
                label={
                  <>
                    {t('pages.admin.offers.fields.name')}
                    <span className="ml-0.5 text-rose-500">*</span>
                  </>
                }
                placeholder={t('pages.admin.offers.fields.namePlaceholder')}
              />
            )}
          />
          <p className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
            {t('pages.admin.offers.sections.identity')}
          </p>
          <FormField
            control={form.control}
            name="alias"
            render={({ error, field }) => (
              <FormInput
                {...field}
                className="border-border/60 bg-background/60 font-mono focus-visible:ring-indigo-500/40 focus-visible:ring-offset-0"
                error={error}
                id="offer-alias"
                label={
                  <>
                    {t('pages.admin.offers.fields.alias')}
                    <span className="ml-0.5 text-rose-500">*</span>
                  </>
                }
                placeholder={t('pages.admin.offers.fields.aliasPlaceholder')}
              />
            )}
          />
          {!values.provider_id && (
            <p className="rounded-xl border border-dashed border-indigo-500/30 bg-indigo-500/5 px-4 py-6 text-center text-sm text-muted-foreground">
              {t('pages.admin.offers.form.lockedProvider')}
            </p>
          )}
          {!!values.provider_id && (
            <OfferFormGroup
              open={coverageOpen}
              summary={[
                values.regions.join(', '),
                values.countries.join(', '),
                values.currencies.join(', '),
                values.traffic_type,
                values.integration_methods.join(', '),
              ]
                .map((item) => item.trim())
                .filter(Boolean)
                .join(' · ')}
              title={t('pages.admin.offers.sections.coverage')}
              onToggle={() => setCoverageOpen((open) => !open)}>
              <OfferCoverageSection
                baseOptions={values.currencies.map((code) => ({
                  label: code,
                  value: code,
                }))}
                control={form.control}
                countryOptions={countryOptions}
                currencyOptions={codesToOptions(
                  provider?.currencies,
                  catalog?.currencies,
                )}
                exceptionOptions={exceptionOptions}
                integrationOptions={codesToOptions(
                  provider?.integration_methods,
                  catalog?.integration_methods,
                )}
                regionOptions={regionOptions}
                showExceptions={values.regions.length > 0}
              />
            </OfferFormGroup>
          )}
          {!!values.provider_id && !values.base_currency && (
            <p className="rounded-xl border border-dashed border-indigo-500/30 bg-indigo-500/5 px-4 py-6 text-center text-sm text-muted-foreground">
              {t('pages.admin.offers.form.lockedCurrency')}
            </p>
          )}
          {!!values.base_currency && (
            <>
              <OfferFormGroup
                open={pricingOpen}
                title={t('pages.admin.offers.sections.pricing')}
                onToggle={() => setPricingOpen((open) => !open)}>
                <OfferPricingSection
                  control={form.control}
                  methodOptions={codesToOptions(
                    provider?.payment_methods,
                    catalog?.payment_methods,
                  )}
                  setValue={form.setValue}
                  values={values}
                />
              </OfferFormGroup>
              <OfferFormGroup
                open={feesOpen}
                title={t('pages.admin.offers.sections.fees')}
                onToggle={() => setFeesOpen((open) => !open)}>
                <OfferFeesSection
                  control={form.control}
                  currency={values.base_currency}
                  setValue={form.setValue}
                  values={values}
                />
              </OfferFormGroup>
            </>
          )}
        </div>
        <DialogFooter
          className="shrink-0 flex-row items-center justify-between gap-2 border-t border-border/40 bg-card px-4 py-3 sm:flex-row sm:px-6"
          endSlot={
            <Button
              className={
                step
                  ? 'h-10 min-w-0 flex-1 bg-foreground/10 text-muted-foreground shadow-none hover:bg-foreground/10 disabled:opacity-100'
                  : 'h-10 min-w-0 flex-1 bg-gradient-to-r from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-500/25 hover:opacity-95'
              }
              disabled={Boolean(step) || submitting}
              type="submit">
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : step ? (
                t(step)
              ) : isEdit ? (
                t('pages.admin.offers.form.saveAction')
              ) : (
                t('pages.admin.offers.form.createAction')
              )}
            </Button>
          }
          startSlot={
            <button
              className="h-10 shrink-0 cursor-pointer rounded-xl px-3 text-sm font-medium text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
              type="button"
              onClick={onClose}>
              {t('buttons.cancel')}
            </button>
          }
        />
      </form>
    </Dialog>
  );
};

OfferFormModal.displayName = 'OfferFormModal';
